import { BROOD_EGG_REST } from '../../models/brood-queen.ts';
import { clip, type Bones, type Clip } from '../clip.ts';
import { TAU, envelope, inCubic, inExpo, inOut, inQuad, outBack, outCubic, outExpo, outQuad, phase, settle, span } from '../curves.ts';
import { timeline } from '../pose.ts';

/*
 * La Madre Tejedora (models/brood-queen.ts): heavy where the weaver is quick. Her legs follow the weaver's
 * conventions (each leg's local +Z runs out along it: −X on `leg<i>` lifts it, a Y turn swings it, +X on
 * `knee<i>`/`foot<i>` folds them in; legs 0–3 the right side front to back, 4–7 the left). +X on the body
 * pitches the head down (−X rears); +X on the abdomen lifts its tail, − lowers it to the floor; −X on the fangs
 * opens them.
 *
 * 'idle' (4 s): breathing, the front legs feeling the air; 'walk' (1.4 s): the alternating tetrapod, slow;
 * 'attack': rears and bites (0.5 s). The rule: 'lay' (2.0 s): she settles low, curls her abdomen under and a
 * cocoon swells at her spinnerets (the `egg` bone, built tiny: full by 1.3 s), set on the floor (1.55 s) and
 * gone by 1.75 s (the `cocoon` prop takes its place there).
 */

type L = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7;
type B = 'root' | 'body' | 'head' | 'chelL' | 'chelR' | 'abdomen' | 'egg' | `leg${L}` | `knee${L}` | `foot${L}`;
const LEGS = [0, 1, 2, 3, 4, 5, 6, 7] as const;
const EGG = 1 / BROOD_EGG_REST;

const side = (i: number): number => (i < 4 ? 1 : -1);
const row = (i: number): number => i % 4;
const tetra = (i: number): number => (i === 0 || i === 2 || i === 5 || i === 7 ? 0 : 1);

/** One leg's channels: `lift` raises it, `stride` swings the foot forward (+) or back (−), `splay` lowers the body between them, `curl` folds it in. */
const legCh = (i: number, lift: number, stride: number, splay = 0, curl = 0): [[number, number, number], [number, number, number], [number, number, number]] => [
  [-0.24 * lift + 0.18 * splay + 1.1 * curl, -side(i) * stride * (row(i) === 0 || row(i) === 3 ? 0.26 : 0.22), 0],
  [0.12 * lift - 0.26 * splay + 0.95 * curl, 0, 0],
  [0.14 * lift + 0.1 * splay + 0.85 * curl, 0, 0],
];
const leg = (b: Bones<B>, i: number, lift: number, stride: number, splay = 0, curl = 0): void => {
  const [h, k, f] = legCh(i, lift, stride, splay, curl);
  b[`leg${i as L}`].rot(...h);
  b[`knee${i as L}`].rot(...k);
  b[`foot${i as L}`].rot(...f);
};
const fangs = (b: Bones<B>, o: number): void => {
  b.chelL.rot(-o, 0, 0.3 * o);
  b.chelR.rot(-o, 0, -0.3 * o);
};

const idle = (): Clip<B> =>
  clip<B>('idle', 4, true, (b, t) => {
    const ph = phase(t, 4);
    const p = ph * TAU;
    const br = Math.sin(p * 2);
    b.abdomen.rot(0.03 * br, 0.04 * Math.sin(p), 0).scale(1 + 0.02 * br);
    b.body.pos(0, 0.01 * br, 0).rot(0.02 * Math.sin(p), 0, 0);
    b.head.rot(0.03 * Math.sin(p + 1), 0.12 * Math.sin(p), 0);
    for (const [i, a] of [[0, 0.1], [4, 0.6]] as const) {
      const k = envelope(ph, a, a + 0.08, a + 0.22, a + 0.3);
      leg(b, i, 1.6 * k + 0.2 * Math.sin(TAU * 5 * t + i) * k, 0.4 * k, 0);
    }
    fangs(b, 0.05 + 0.06 * Math.max(0, Math.sin(p * 2 + 1)) ** 3);
  }, (t) => {
    const br = Math.sin(phase(t, 4) * TAU * 2);
    return [1 + 0.004 * br, 1 - 0.006 * br, 1 + 0.004 * br];
  });

const walk = (dur = 1.4): Clip<B> =>
  clip<B>('walk', dur, true, (b, t) => {
    const ph = phase(t, dur);
    const th = ph * TAU;
    for (const i of LEGS) {
      const u = (ph + (tetra(i) ? 0.5 : 0) - row(i) * 0.05 + 1) % 1;
      const swing = u >= 0.5;
      const k = swing ? (u - 0.5) / 0.5 : u / 0.5;
      const stride = swing ? -Math.cos(Math.PI * k) : Math.cos(Math.PI * k);
      const lift = swing ? Math.sin(Math.PI * k) ** 0.7 : 0;
      leg(b, i, 1.0 * lift, 1.1 * stride, 0.05);
    }
    b.root.pos(0, -0.01 - 0.012 * Math.cos(2 * th), 0);
    b.body.rot(0.02 * Math.sin(2 * th - 0.8), 0.04 * Math.sin(th), 0.03 * Math.sin(th - 0.3));
    b.abdomen.rot(0.04 * Math.cos(2 * th - 1.4), -0.09 * Math.sin(th - 1.0), 0.04 * Math.sin(th - 1.2));
    b.head.rot(0, -0.05 * Math.sin(th), 0);
  });

const attack = (): Clip<B> =>
  clip<B>('attack', 1.3, false, (b, t) => {
    const rear = (k: number): void => {
      b.body.rot(-0.35 * k, 0, 0);
      b.abdomen.rot(0.25 * k, 0, 0);
      leg(b, 0, 2.2 * k, 0.6 * k);
      leg(b, 4, 2.2 * k, 0.6 * k);
      leg(b, 1, 0.8 * k, 0.3 * k);
      leg(b, 5, 0.8 * k, 0.3 * k);
      for (const i of [2, 3, 6, 7]) leg(b, i, 0, -0.2 * k, -0.4 * k);
    };
    const up = span(t, 0, 0.38, inOut) * (1 - span(t, 0.5, 0.6, outExpo));
    const strike = envelope(t, 0.42, 0.5, 0.7, 1.2, outCubic);
    rear(up);
    b.body.rot(0.25 * strike, 0, 0).pos(0, -0.05 * strike, 0.12 * strike);
    leg(b, 0, -0.5 * strike, 0.7 * strike);
    leg(b, 4, -0.5 * strike, 0.7 * strike);
    fangs(b, 0.5 * up - 0.15 * strike);
    b.head.rot(-0.2 * up + 0.15 * strike, 0, 0);
  }, (t) => {
    const q = Math.max(0, settle(t - 0.5, 3, 8)) * (t > 0.5 ? 1 : 0);
    return [1 + 0.03 * q, 1 - 0.04 * q, 1 + 0.03 * q];
  });

/** The rule: she settles, curls her abdomen down and lays a cocoon on the floor behind her. */
const lay = (): Clip<B> =>
  clip<B>('lay', 2.0, false, (b, t) => {
    const low = span(t, 0, 0.45, inOut) * (1 - span(t, 1.6, 2.0, inOut));
    const curl = span(t, 0.2, 0.7, inOut) * (1 - span(t, 1.55, 1.9, inOut));
    b.root.pos(0, -0.22 * low, 0);
    for (const i of LEGS) leg(b, i, 0, 0, 1.0 * low);
    b.body.rot(0.12 * low, 0, 0);
    b.head.rot(0.08 * low, 0.1 * Math.sin(t * 5) * low, 0);
    // The abdomen bows down to the floor and pulses as the cocoon comes.
    const push = Math.max(0, Math.sin((t - 0.6) * 9)) * envelope(t, 0.6, 0.7, 1.2, 1.35);
    b.abdomen.rot(-0.6 * curl + 0.05 * push, 0, 0).scale(1 + 0.05 * push);
    const grow = span(t, 0.55, 1.3, outBack) * (1 - span(t, 1.6, 1.75, inQuad));
    b.egg.pos(0, -0.18 * span(t, 1.3, 1.55, inQuad), 0).scale(1 + (EGG - 1) * Math.max(0.001, grow));
    fangs(b, 0.08);
  }, (t) => {
    const q = Math.max(0, settle(t - 1.55, 3, 8)) * (t > 1.55 ? 1 : 0);
    return [1 + 0.02 * q, 1 - 0.03 * q, 1 + 0.02 * q];
  }, 'Pone un capullo');

const hit = (): Clip<B> =>
  clip<B>('hit', 0.5, false, (b, t) => {
    const q = settle(t, 2.5, 8) * span(t, 0, 0.04, outQuad);
    b.root.pos(0, 0, -0.05 * q);
    b.body.rot(-0.15 * q, 0.06 * q, 0);
    b.abdomen.rot(0.1 * q, -0.08 * q, 0);
    for (const i of LEGS) leg(b, i, 0.3 * Math.max(0, q), 0, 0);
    fangs(b, 0.3 * Math.max(0, q));
  });

const stun = (): Clip<B> =>
  clip<B>('stun', 2.6, true, (b, t) => {
    const p = phase(t, 2.6) * TAU;
    b.root.pos(0, -0.12, 0);
    for (const i of LEGS) leg(b, i, 0, 0.1 * Math.sin(p + i), 0.6 + 0.08 * Math.sin(p * 2 + i));
    b.body.rot(0.15, 0.1 * Math.sin(p), 0.05 * Math.cos(p));
    b.abdomen.rot(-0.1, 0.06 * Math.sin(p + 1), 0);
    fangs(b, 0.2);
  });

/** Death (2.6 s): she rears with her legs thrashing, then drops, rolls onto her back and the legs curl in. */
const die = (): Clip<B> =>
  clip<B>('die', 2.6, false, (b, t) => {
    const thrash = envelope(t, 0, 0.2, 0.7, 1.0);
    for (const i of LEGS) {
      const curl = span(t, 1.2, 2.2, inCubic);
      leg(b, i, thrash * (1.2 + 0.6 * Math.sin(t * 18 + i * 1.3)), thrash * 0.5 * Math.sin(t * 14 + i), 0, 1.3 * curl);
    }
    b.body.rot(-0.3 * thrash, 0, 0);
    fangs(b, 0.6 * thrash + 0.3 * span(t, 1.0, 2.0));
    timeline(b, t, [
      [0, {}],
      [1.0, { root: [0, 0, 0, 0, -0.1, 0] }, inOut],
      [1.9, { root: [0, 0, 2.85, 0, 1.4, 0], abdomen: [-0.3, 0, 0] }, inExpo],
      [2.15, { root: [0, 0, 2.9, 0, 1.36, 0], abdomen: [-0.25, 0, 0] }, outBack],
      [2.6, { root: [0, 0, 2.9, 0, 1.38, 0], abdomen: [-0.28, 0, 0] }, inOut],
    ]);
  }, (t) => {
    const q = Math.max(0, settle(t - 1.9, 2.5, 7)) * (t > 1.9 ? 1 : 0);
    return [1 + 0.04 * q, 1 - 0.05 * q, 1 + 0.04 * q];
  });

/** Clips by library kind (see ../rigs.ts). */
export const broodQueenClips = (): Clip[] => [idle(), walk(), attack(), hit(), stun(), die(), lay()] as Clip[];
export const CLIPS: Record<string, () => Clip[]> = { broodQueen: broodQueenClips };
